import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'antd';
import i18n from 'i18next';

import { getBalance, getLoyalty, getInvestmentStatistic, getDocuments } from './actions';
import { logout } from '../authorization/actions';
import { deleteAccountConfirm } from '../userSettings/actions';

import { Loyalty, Overview, Investments } from 'models';

import { apiReducerBuilder } from 'utils';

type Documents = {
	das1: { document_type: string, expired: number }[];
	identity: { expired: 0 | 1; valid_until: '' }[];
	company: { expired: 0 | 1; valid_until: '' }[];
};

const initialState = {
	status: '',
	data: {} as Overview,
	investments: {
		status: '',
		data: [] as Investments,
	},
	loyalty: [] as Loyalty,
	timeBalanceLoaded: 0,
	documents: {} as Documents,
};

export const overviewSlice = createSlice({
	name: 'overview',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		apiReducerBuilder(builder, getDocuments, (state, action) => {
			const { payload, meta } = action;

			state.status = meta.requestStatus;
			state.documents = payload;
		});

		builder.addCase(getBalance.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});

		builder.addCase(getBalance.fulfilled, (state, action) => {
			const { meta, payload }: { [key: string]: any } = action;
			state.status = meta.requestStatus;
			state.data = payload;
			state.timeBalanceLoaded = new Date().getTime();
		});

		builder.addCase(getLoyalty.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});

		builder.addCase(getLoyalty.fulfilled, (state, action) => {
			const { meta, payload }: { [key: string]: any } = action;

			state.status = meta.requestStatus;
			state.loyalty = payload.items;
		});

		builder.addCase(getInvestmentStatistic.rejected, (state, action) => {
			const { meta, error } = action;
			state.investments.status = meta.requestStatus;
			message.warn(error.message);
		});

		builder.addCase(getInvestmentStatistic.pending, (state, action) => {
			const { meta } = action;
			state.investments.status = meta.requestStatus;
		});

		builder.addCase(getInvestmentStatistic.fulfilled, (state, action) => {
			const { meta, payload }: { [key: string]: any } = action;
			state.investments.status = meta.requestStatus;

			state.investments.data =
				meta.arg === 'investment_statuses'
					? Object.keys(payload).map((item) => {
							return {
								type:
									item === 'current'
										? i18n.t('currentText')
										: i18n.t('daysLateText', {
												item: item === 'more than 90 days late' ? i18n.t('moreThan90Days') : item,
										  }),
								...payload[item],
							};
					  })
					: payload;
		});

		builder.addMatcher(isAnyOf(getBalance.rejected, getLoyalty.rejected), (state, action) => {
			const { meta, error } = action;
			state.status = meta.requestStatus;
			message.warn(error.message);
		});

		builder.addMatcher(isAnyOf(logout, deleteAccountConfirm.fulfilled), () => {
			return initialState;
		});
	},
});

export default overviewSlice.reducer;
